import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export interface TransitionStateRequest {
  trigger: string;
  approvalDate?: string;
  outdoorExposureTestStartDate?: string;
  cancellationReason?: string;
  comment?: string; // Optional comment for promotion transitions
  documentIds?: string[]; // Optional document IDs to associate with the transition
}

export interface TransitionStateResponse {
  previousState: string;
  newState: string;
  completeApprovalNumber: string;
  certificateExpiryDate?: string;
}

export interface AvailableTransition {
  trigger: string;
  targetState: string;
  description: string;
}

export interface AvailableTransitionsResponse {
  currentState: string;
  availableTransitions: AvailableTransition[];
}

export interface GenerateCertificateResponse {
  documentId: string;
  approvalNumber: string;
  fileName: string;
  fileSizeBytes: number;
}

export interface RenewCertificateRequest {
  newExpiryDate: string; // Required - user must provide expiry date
  notes?: string;
}

export interface RenewCertificateResponse {
  documentId: string;
  approvalNumber: string;
  previousExpiryDate: string;
  newExpiryDate: string;
  renewalCount: number;
  fileName: string;
}

// Color management types (Phase 2)
export type ColorApprovalStatus = 'Provisional' | 'Interim' | 'Final' | 'Suspended' | 'Banned';

export interface AddColorRequest {
  colorNumber: string;
  status?: ColorApprovalStatus;
  pmCanProduceBannedUntil?: string;
  pmCanSellBannedUntil?: string;
  coaterCanUseBannedUntil?: string;
  pmCanProduceRemainingUntil?: string;
  pmCanSellRemainingUntil?: string;
  coaterCanUseRemainingUntil?: string;
  referenceNumber?: string;
  notes?: string;
}

export interface UpdateColorRequest {
  status?: ColorApprovalStatus;
  pmCanProduceBannedUntil?: string;
  pmCanSellBannedUntil?: string;
  coaterCanUseBannedUntil?: string;
  pmCanProduceRemainingUntil?: string;
  pmCanSellRemainingUntil?: string;
  coaterCanUseRemainingUntil?: string;
  referenceNumber?: string;
  notes?: string;
}

export interface ColorOperationResponse {
  systemId: string;
  colorNumber: string;
  status?: ColorApprovalStatus;
  approvalNumber: string;
}

// System name update types
export interface UpdateNameResponse {
  systemId: string;
  previousName: string;
  newName: string;
  completeApprovalNumber: string;
}

// Bulk certificate renewal types
export interface StartBulkRenewalResponse {
  batchId: string;
  totalSystemsRenewed: number;
  totalSystemsRegenerated: number;
  newExpiryDate: string;
}

export interface BulkRenewalStatusResponse {
  batchId: string;
  totalCount: number;
  pendingCount: number;
  processingCount: number;
  completedCount: number;
  failedCount: number;
  queuedCount: number;
  isComplete: boolean;
  failedItems?: { systemId: string; error: string }[];
}

export interface FinalSystemsCountResponse {
  count: number;
}

// Bulk certificate download types
export type CertificateTypeFilter = 'All' | 'CoatingSystems' | 'PretreatmentSystems';

export interface StartBulkDownloadRequest {
  certificationYear: number;
  typeFilter: CertificateTypeFilter;
}

export interface StartBulkDownloadResponse {
  batchId: string | null;
  totalCertificates: number;
  message?: string;
}

export interface BulkDownloadStatusResponse {
  batchId: string;
  totalCount: number;
  processedCount: number;
  progressPercent: number;
  isComplete: boolean;
  isFailed: boolean;
  error?: string;
  zipBlobRef?: string;
}

export interface BulkDownloadCountResponse {
  count: number;
}

// Create system types
export interface NextSystemNumberResponse {
  nextNumber: number;
  prefix: string;
  formattedNumber: string;
}

export interface CheckNumberExistsResponse {
  exists: boolean;
  formattedNumber: string;
}

// P-Code validation types (for Powder Number inspection workflow)
export interface NextPCodeResponse {
  nextNumber: number;
  prefix: string;
  suggestedPCode: string;
}

export interface CheckPCodeExistsResponse {
  exists: boolean;
  pCode: string;
}

export interface CreateSystemRequest {
  systemType: string;
  // Optional for class-3 coating systems (created in Granting state; number assigned at final grant).
  number?: number;
  productionSiteId: string;
  system: string;
  approvalDate?: string;
  // Pretreatment-specific
  outdoorExposureTestStartDate?: string;
  rinseProcess?: string;
  // Coating system-specific
  pCode?: string;
  pClass?: number;
  glossCategory?: number;
  structureFinish?: string;
  coatingMaterial?: number;
  approvalType?: number;
  metallicColoursApproved?: boolean;
}

export interface CreateSystemResponse {
  systemId: string;
  completeApprovalNumber: string;
  approvalState: string;
  supplierOrManufacturerOrganisationId: string;
  supplierOrManufacturerName: string;
  // Null for class-3 coating systems created in Granting state.
  provisionalApprovalDate?: string;
  certificateExpiryDate?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApprovedSystemsService {
  private http = inject(HttpClient);
  private baseUrl = '/api/certifiable-systems';

  /**
   * Transition a system to a new approval state
   */
  transitionState(systemId: string, request: TransitionStateRequest): Observable<TransitionStateResponse> {
    return this.http.post<TransitionStateResponse>(`${this.baseUrl}/${systemId}/transition`, request);
  }

  /**
   * Get available state transitions for a system
   */
  getAvailableTransitions(systemId: string): Observable<AvailableTransitionsResponse> {
    return this.http.get<AvailableTransitionsResponse>(`${this.baseUrl}/${systemId}/available-transitions`);
  }

  /**
   * Cancel a system approval
   */
  cancelSystem(
    systemId: string,
    cancellationReason: string,
    cancellationDate?: Date,
    documentIds?: string[]
  ): Observable<TransitionStateResponse> {
    return this.http.post<TransitionStateResponse>(`${this.baseUrl}/${systemId}/cancel`, {
      cancellationReason,
      cancellationDate: cancellationDate?.toISOString(),
      documentIds,
    });
  }

  /**
   * Archive a system (soft delete)
   */
  archiveSystem(systemId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${systemId}/archive`, {});
  }

  /**
   * Restore an archived system
   */
  unarchiveSystem(systemId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${systemId}/unarchive`, {});
  }

  /**
   * Generate a PDF certificate for an approved system
   */
  generateCertificate(systemId: string): Observable<GenerateCertificateResponse> {
    return this.http.post<GenerateCertificateResponse>(`${this.baseUrl}/${systemId}/generate-certificate`, {});
  }

  /**
   * Renew the certificate for an approved system (extends expiry date)
   * @param systemId The system ID
   * @param request Request with new expiry date (required) and optional notes
   */
  renewCertificate(systemId: string, request: RenewCertificateRequest): Observable<RenewCertificateResponse> {
    return this.http.post<RenewCertificateResponse>(`${this.baseUrl}/${systemId}/renew-certificate`, request);
  }

  // ============================================
  // Color Management Methods (Phase 2)
  // ============================================

  /**
   * Add a banned or suspended color to a coating system
   */
  addColor(systemId: string, request: AddColorRequest): Observable<ColorOperationResponse> {
    return this.http.post<ColorOperationResponse>(`${this.baseUrl}/${systemId}/colors`, request);
  }

  /**
   * Update a banned or suspended color on a coating system
   */
  updateColor(systemId: string, colorNumber: string, request: UpdateColorRequest): Observable<ColorOperationResponse> {
    return this.http.put<ColorOperationResponse>(
      `${this.baseUrl}/${systemId}/colors/${encodeURIComponent(colorNumber)}`,
      request
    );
  }

  /**
   * Remove a banned or suspended color from a coating system
   */
  removeColor(systemId: string, colorNumber: string, notes?: string): Observable<ColorOperationResponse> {
    return this.http.delete<ColorOperationResponse>(
      `${this.baseUrl}/${systemId}/colors/${encodeURIComponent(colorNumber)}`,
      { body: notes ? { notes } : undefined }
    );
  }

  // ============================================
  // System Name Update
  // ============================================

  /**
   * Update the system name
   */
  updateName(systemId: string, name: string): Observable<UpdateNameResponse> {
    return this.http.put<UpdateNameResponse>(`${this.baseUrl}/${systemId}/name`, { name });
  }

  // ============================================
  // Bulk Certificate Renewal Methods
  // ============================================

  /**
   * Get count of systems in Final state eligible for bulk renewal
   */
  getFinalSystemsCount(): Observable<FinalSystemsCountResponse> {
    return this.http.get<FinalSystemsCountResponse>(`${this.baseUrl}/bulk-certificate-renewal/count`);
  }

  /**
   * Start bulk certificate renewal for all approved systems in Final state
   */
  startBulkRenewal(): Observable<StartBulkRenewalResponse> {
    return this.http.post<StartBulkRenewalResponse>(`${this.baseUrl}/bulk-certificate-renewal`, {});
  }

  /**
   * Get status of a bulk certificate renewal operation
   */
  getBulkRenewalStatus(batchId: string): Observable<BulkRenewalStatusResponse> {
    return this.http.get<BulkRenewalStatusResponse>(`${this.baseUrl}/bulk-certificate-renewal/${batchId}/status`);
  }

  // ============================================
  // Bulk Certificate Download Methods
  // ============================================

  /**
   * Get count of certificates matching the bulk download filter criteria
   */
  getBulkDownloadCertificateCount(
    certificationYear: number,
    typeFilter: CertificateTypeFilter
  ): Observable<BulkDownloadCountResponse> {
    return this.http.get<BulkDownloadCountResponse>(`${this.baseUrl}/bulk-certificate-download/count`, {
      params: {
        certificationYear: certificationYear.toString(),
        typeFilter,
      },
    });
  }

  /**
   * Start bulk certificate download
   */
  startBulkDownload(
    certificationYear: number,
    typeFilter: CertificateTypeFilter
  ): Observable<StartBulkDownloadResponse> {
    const request: StartBulkDownloadRequest = { certificationYear, typeFilter };
    return this.http.post<StartBulkDownloadResponse>(`${this.baseUrl}/bulk-certificate-download`, request);
  }

  /**
   * Get status of a bulk certificate download operation
   */
  getBulkDownloadStatus(batchId: string): Observable<BulkDownloadStatusResponse> {
    return this.http.get<BulkDownloadStatusResponse>(`${this.baseUrl}/bulk-certificate-download/${batchId}/status`);
  }

  /**
   * Download the zip file for a completed bulk certificate download operation
   * This triggers the browser's file download dialog
   */
  downloadBulkCertificates(batchId: string, filename: string): void {
    this.http
      .get(`${this.baseUrl}/bulk-certificate-download/${batchId}/download`, {
        responseType: 'blob',
        observe: 'response',
      })
      .subscribe((response) => {
        if (response.body) {
          const blob = new Blob([response.body], { type: 'application/zip' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
      });
  }

  // ============================================
  // Create System Methods
  // ============================================

  /**
   * Get the next available system number for a given system type
   */
  getNextSystemNumber(systemType: string): Observable<NextSystemNumberResponse> {
    return this.http.get<NextSystemNumberResponse>(`${this.baseUrl}/next-number`, {
      params: { systemType },
    });
  }

  /**
   * Check if a system number already exists for a given system type
   */
  checkNumberExists(systemType: string, number: number): Observable<CheckNumberExistsResponse> {
    return this.http.get<CheckNumberExistsResponse>(`${this.baseUrl}/check-number`, {
      params: { systemType, number: number.toString() },
    });
  }

  /**
   * Get the next available P-Code number for a given manufacturer prefix.
   * Used in the Powder Number inspection workflow to suggest a P-Code.
   */
  getNextPCode(prefix: string): Observable<NextPCodeResponse> {
    return this.http.get<NextPCodeResponse>(`${this.baseUrl}/next-pcode`, {
      params: { prefix },
    });
  }

  /**
   * Check if a P-Code already exists.
   * Used in the Powder Number inspection workflow to validate user input.
   */
  checkPCodeExists(pCode: string): Observable<CheckPCodeExistsResponse> {
    return this.http.get<CheckPCodeExistsResponse>(`${this.baseUrl}/check-pcode`, {
      params: { pCode },
    });
  }

  /**
   * Create a new certifiable system (System Approval)
   */
  createSystem(request: CreateSystemRequest): Observable<CreateSystemResponse> {
    return this.http.post<CreateSystemResponse>(this.baseUrl, request);
  }
}
